import React, { useEffect, useMemo, useRef, useState } from 'react';
import './ElevationProfile.css';

const HEIGHT = 180;
const PAD = { top: 12, right: 16, bottom: 26, left: 52 };

const EARTH_RADIUS_M = 6371008.8;
const toRad = (d) => (d * Math.PI) / 180;

function haversine(a, b) {
  const dLat = toRad(b[1] - a[1]);
  const dLon = toRad(b[0] - a[0]);
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(a[1])) * Math.cos(toRad(b[1])) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(h));
}

// Round axis steps: 1, 2 or 5 times a power of ten, aiming for ~`target` ticks.
function niceStep(span, target) {
  const raw = span / Math.max(target, 1);
  const pow = 10 ** Math.floor(Math.log10(raw || 1));
  const m = raw / pow;
  return (m <= 1 ? 1 : m <= 2 ? 2 : m <= 5 ? 5 : 10) * pow;
}

function ticks(min, max, target) {
  const step = niceStep(max - min, target);
  const out = [];
  for (let v = Math.ceil(min / step) * step; v <= max + 1e-9; v += step) out.push(v);
  return out;
}

/**
 * Distance-vs-height profile of one run or lift, read from the Z of its
 * LineString coordinates. Vertices with unknown height (null Z) break the line.
 *
 * `onHover(coord | null)` reports the vertex under the crosshair so the map can
 * mark the same point.
 */
const ElevationProfile = ({ coordinates, color, onHover }) => {
  const wrapRef = useRef(null);
  const [width, setWidth] = useState(600);
  const [hoverIdx, setHoverIdx] = useState(null);

  useEffect(() => {
    if (!wrapRef.current) return;
    const ro = new ResizeObserver(([entry]) => setWidth(Math.max(240, entry.contentRect.width)));
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => { setHoverIdx(null); }, [coordinates]);

  const points = useMemo(() => {
    let dist = 0;
    return coordinates.map((c, i) => {
      if (i > 0) dist += haversine(coordinates[i - 1], c);
      const z = c.length > 2 && Number.isFinite(c[2]) ? c[2] : null;
      return { d: dist, z, coord: c };
    });
  }, [coordinates]);

  const known = points.filter(p => p.z != null);
  if (points.length < 2 || known.length < 2) {
    return (
      <div className="ep" ref={wrapRef}>
        <div className="ep-empty">No elevation data for this feature.</div>
      </div>
    );
  }

  const zs = known.map(p => p.z);
  const zMinRaw = Math.min(...zs);
  const zMaxRaw = Math.max(...zs);
  const zPad = Math.max(5, (zMaxRaw - zMinRaw) * 0.08);
  const yStep = niceStep(zMaxRaw - zMinRaw + 2 * zPad, 4);
  const zMin = Math.floor((zMinRaw - zPad) / yStep) * yStep;
  const zMax = Math.ceil((zMaxRaw + zPad) / yStep) * yStep;
  const dMax = points[points.length - 1].d || 1;

  const plotW = width - PAD.left - PAD.right;
  const plotH = HEIGHT - PAD.top - PAD.bottom;
  const x = (d) => PAD.left + (d / dMax) * plotW;
  const y = (z) => PAD.top + (1 - (z - zMin) / (zMax - zMin)) * plotH;

  // One subpath per run of known heights.
  let path = '';
  let pen = false;
  for (const p of points) {
    if (p.z == null) { pen = false; continue; }
    path += `${pen ? 'L' : 'M'}${x(p.d).toFixed(1)},${y(p.z).toFixed(1)}`;
    pen = true;
  }

  const yTicks = ticks(zMin, zMax, 4);
  const xTicks = ticks(0, dMax, Math.max(2, Math.floor(plotW / 110)));

  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const d = ((e.clientX - rect.left - PAD.left) / plotW) * dMax;
    let best = null;
    points.forEach((p, i) => {
      if (p.z == null) return;
      if (best == null || Math.abs(p.d - d) < Math.abs(points[best].d - d)) best = i;
    });
    setHoverIdx(best);
    if (onHover) onHover(best == null ? null : points[best].coord);
  };
  const onLeave = () => {
    setHoverIdx(null);
    if (onHover) onHover(null);
  };

  const hp = hoverIdx != null ? points[hoverIdx] : null;
  const tipLeft = hp ? Math.min(Math.max(x(hp.d), PAD.left + 60), width - PAD.right - 60) : 0;

  return (
    <div className="ep" ref={wrapRef}>
      <svg width={width} height={HEIGHT} role="img" aria-label="Elevation profile">
        {yTicks.map(t => (
          <g key={`y${t}`}>
            <line className="ep-grid" x1={PAD.left} x2={width - PAD.right} y1={y(t)} y2={y(t)} />
            <text className="ep-axis" x={PAD.left - 8} y={y(t)} textAnchor="end" dominantBaseline="middle">
              {Math.round(t).toLocaleString()} m
            </text>
          </g>
        ))}
        {xTicks.map(t => (
          <text key={`x${t}`} className="ep-axis" x={x(t)} y={HEIGHT - 8} textAnchor="middle">
            {t >= 1000 ? `${(t / 1000).toLocaleString()} km` : `${Math.round(t)} m`}
          </text>
        ))}
        <path d={path} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        {hp && (
          <g>
            <line className="ep-crosshair" x1={x(hp.d)} x2={x(hp.d)} y1={PAD.top} y2={PAD.top + plotH} />
            <circle cx={x(hp.d)} cy={y(hp.z)} r={4} fill={color} stroke="#0f172a" strokeWidth={2} />
          </g>
        )}
        <rect
          x={PAD.left} y={PAD.top} width={plotW} height={plotH} fill="transparent"
          onMouseMove={onMove} onMouseLeave={onLeave}
        />
      </svg>
      {hp && (
        <div className="ep-tip" style={{ left: tipLeft }}>
          <strong>{Math.round(hp.z).toLocaleString()} m</strong>
          <span>{hp.d >= 1000 ? `${(hp.d / 1000).toFixed(2)} km` : `${Math.round(hp.d)} m`} along</span>
        </div>
      )}
    </div>
  );
};

export default ElevationProfile;
